import { Table, type TableProps } from 'antd';
import { type Dispatch, type Key, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type InsuranceModuleSpecsFragment } from '../../../../api/fragments/InsuranceModuleSpecs';
import { type InsurerListDataFragment } from '../../../../api/fragments/InsurerListData';
import PaginatedTable, { type PageAction } from '../../../../components/PaginatedTable';
import renderBooleanIcon from '../../../../utilities/renderBooleanIcon';
import { type State } from '../../InsurerListPage/useListReducer';

export type InsuranceModuleTableProps = {
    module: InsuranceModuleSpecsFragment;
    dataSource: InsurerListDataFragment[];
    state: State;
    dispatch: Dispatch<PageAction>;
    total: number;
};

type ModuleDataSource = InsurerListDataFragment & { key: Key };
type InsurerTable = TableProps<ModuleDataSource>;

const InsuranceModuleTable = ({ module, dataSource, dispatch, state, total }: InsuranceModuleTableProps) => {
    const { t } = useTranslation(['insuranceModuleDetails', 'insurerList']);
    const navigate = useNavigate();

    const onRow = useCallback<InsurerTable['onRow']>(
        record => ({
            onClick: () => navigate(`/admin/insurers/${record.id}`),
        }),
        [navigate]
    );

    return (
        <PaginatedTable
            dataSource={dataSource}
            dispatch={dispatch}
            onRow={onRow}
            rowKey="id"
            state={state}
            tableName={t('insurerList:title')}
            total={total}
        >
            <Table.Column
                key="displayName"
                dataIndex="displayName"
                title={t('insuranceModuleDetails:insuranceModuleTable.columns.displayName')}
            />
            <Table.Column
                key="order"
                dataIndex="order"
                title={t('insuranceModuleDetails:insuranceModuleTable.columns.order')}
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                title={t('insuranceModuleDetails:insuranceModuleTable.columns.isActive')}
                width={120}
            />
        </PaginatedTable>
    );
};

export default InsuranceModuleTable;
